/* ============================================================
   Match My Outfit — Quiz Controller (Alpine v3)
   AUTHORITY: Quiz State Machine (ONLY)

   Owns:
   - Question order + flow
   - Step progression
   - Answer collection
   - localStorage contract
   - Redirect to results page
   - Upload helper behavior (within quiz root)

   Must NOT:
   - Control Alpine lifecycle (no Alpine.start / initTree / hydration retries)
   - Cause cross-page side effects (no BFCache reload handlers)
============================================================ */

(() => {
  "use strict";

  const VERSION = "mmo_quiz_controller_v3_authority_pure";
  const STORAGE_KEY = "banglez_mmo_quiz_state_v3";
  const VERSION_KEY = "banglez_mmo_quiz_version";
  const RESULTS_URL = "/pages/match-my-outfit-results";

  // Hard single-load guard (prevents duplicate registration if script is injected twice)
  if (window.__BANGLEZ_MMO_QUIZ_CONTROLLER_V3__) return;
  window.__BANGLEZ_MMO_QUIZ_CONTROLLER_V3__ = true;

  const clone = (o) => JSON.parse(JSON.stringify(o));
  const safeParse = (s, fb) => {
    try { return JSON.parse(s); } catch { return fb; }
  };

  const safeGet = (k) => {
    try { return localStorage.getItem(k); } catch { return null; }
  };

  const safeSet = (k, v) => {
    try { localStorage.setItem(k, v); return true; } catch { return false; }
  };

  const safeRemove = (k) => {
    try { localStorage.removeItem(k); } catch {}
  };

  function buildResultsUrl() {
    return `${RESULTS_URL}?t=${Date.now()}`;
  }

  function isGift(a) {
    return a.for_whom === "I’m shopping for a gift" || a.occasion === "Gift for a special moment";
  }

  function applyPronouns(str, giftMode) {
    if (typeof str !== "string") return str;

    const map = giftMode
      ? ({
          "{you_subject}": "they",
          "{you_object}": "them",
          "{your_adjective}": "their",
          "{your_pronoun}": "theirs",
        })
      : ({
          "{you_subject}": "you",
          "{you_object}": "you",
          "{your_adjective}": "your",
          "{your_pronoun}": "yours",
        });

    let out = str;
    for (const k of Object.keys(map)) out = out.split(k).join(map[k]);
    return out;
  }

  const BANGLE_SIZES = ["2.2", "2.4", "2.6", "2.8", "2.10", "2.12"];

  const QUESTIONS = [
    {
      id: "occasion",
      title: "What brings you here today?",
      helper: "Pick the closest match - we’ll do the rest.",
      options: [
        "I’m the bride",
        "Wedding - close family",
        "Wedding - guest",
        "Engagement / pre-wedding event",
        "Festive or cultural celebration",
        "Gift for a special moment",
        "Just exploring",
      ],
    },
    {
      id: "for_whom",
      title: "Who are you shopping for?",
      helper: "This helps us personalize the wording and recommendations.",
      options: ["I’m shopping for myself", "I’m shopping for a gift"],
    },
    {
      id: "primary_objective",
      title: "What are you shopping for?",
      helper: "Choose one - we’ll recommend matching pieces.",
      options: ["Complete look", "Bangles", "Necklace set", "Earrings", "Accessories", "I’m not sure"],
    },
    {
      id: "accessory_type",
      title: "Which accessory are you looking for?",
      helper: "Choose one - we’ll match it to {your_adjective} outfit vibe.",
      options: ["Tikka / headpiece", "Nath / nose ring", "Rings", "Handpieces", "Saree belts", "Payal / anklets"],
      condition: (a) => a.primary_objective === "Accessories",
    },
    {
      id: "bangle_size_known",
      title: "Do you know {your_adjective} bangle size?",
      helper: "So we recommend sets you can actually wear confidently.",
      options: ["Yes — I know it", "Not sure — help me measure", "Skip for now"],
      condition: (a) => ["Bangles", "Complete look", "I’m not sure"].includes(a.primary_objective),
    },
    {
      id: "bangle_size_value",
      title: "Select {your_adjective} bangle size",
      helper: "Choose one.",
      options: BANGLE_SIZES,
      condition: (a) => a.bangle_size_known === "Yes — I know it",
    },
    {
      id: "style",
      title: "Which style feels most like {you_object}?",
      helper: "There is no wrong answer.",
      options: ["Minimalist", "Classic", "Glam", "Bold", "Not sure"],
    },
    {
      id: "outfit_colors",
      title: "What colors are in {your_adjective} outfit?",
      helper: "Choose Primary, optional Secondary, optional Accent.",
      type: "color_slots",
      options: [
        "Red","Pink","Maroon","Orange","Yellow","Green","Teal","Blue","Navy",
        "Purple","Black","White / Ivory","Champagne / Beige","Gold","Silver","Rose gold",
      ],
    },
    {
      id: "color_direction",
      title: "How should the jewelry relate to the outfit?",
      helper: "Choose your vibe - we’ll handle the styling rules.",
      options: ["Match exactly", "Complement (harmonize)", "Create contrast", "Not sure"],
    },
  ];

  const buildFlow = (answers) => QUESTIONS.filter((q) => (q.condition ? q.condition(answers) : true));
  const flowToIds = (flow) => (Array.isArray(flow) ? flow.map((q) => q.id) : []);

  function idsToFlow(ids, answers) {
    const fresh = buildFlow(answers);
    if (!Array.isArray(ids) || !ids.length) return fresh;

    const map = new Map(fresh.map((q) => [q.id, q]));
    const ordered = ids.map((id) => map.get(id)).filter(Boolean);

    const seen = new Set(ordered.map((q) => q.id));
    for (const q of fresh) if (!seen.has(q.id)) ordered.push(q);

    return ordered;
  }

  function initialAnswers() {
    return {
      occasion: "",
      for_whom: "",
      primary_objective: "",
      accessory_type: "",
      bangle_size_known: "",
      bangle_size_value: "",
      style: "",
      outfit_colors: [],
      color_direction: "",

      did_upload_outfit: false,
      uploaded_image: "",
      upload: null,
    };
  }

  function persist(state) {
    safeSet(VERSION_KEY, VERSION);
    return safeSet(STORAGE_KEY, JSON.stringify(state));
  }

  // ---- upload helpers
  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async function compressDataUrl(
    dataUrl,
    { maxW = 1600, maxH = 1600, quality = 0.82, mime = "image/jpeg" } = {}
  ) {
    const img = await loadImage(dataUrl);
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;

    const scale = Math.min(1, maxW / w0, maxH / h0);
    const w = Math.max(1, Math.round(w0 * scale));
    const h = Math.max(1, Math.round(h0 * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    return canvas.toDataURL(mime, quality);
  }

  document.addEventListener('click', (e) => {
  const uploadTrigger = e.target.closest('[data-upload-outfit]');
  if (!uploadTrigger) return;

  e.preventDefault();

  const fileInput = document.querySelector('input[type="file"][accept^="image"]');
  if (fileInput) {
    fileInput.click();
  }
});

  // Register ONLY during Alpine init. No lifecycle control beyond that.
  document.addEventListener("alpine:init", () => {
    const Alpine = window.Alpine;
    if (!Alpine) return;

    Alpine.data("mmoQuiz", () => ({
      step: "intro",
      questionIndex: 0,
      answers: initialAnswers(),
      _flow: [],
      colorSlots: [null, null, null],
      showSizeHelp: false,

      get currentQuestion() { return this._flow[this.questionIndex] || null; },
      get progress() {
        const total = this._flow.length || 1;
        const current = Math.min(this.questionIndex + 1, total);
        return `${current} / ${total}`;
      },

      tt(str) { return applyPronouns(str, isGift(this.answers)); },
      get selectedColorCount() { return this.colorSlots.filter(Boolean).length; },
      isColorSelected(opt) { return this.colorSlots.includes(opt); },

      _handleEntryIntent() {
  try {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");

    if (mode === "start") {
      this.startQuiz();
      history.replaceState(null, "", window.location.pathname);
    }

    if (mode === "upload") {
      this.uploadOutfit();
      history.replaceState(null, "", window.location.pathname);
    }
  } catch (e) {
    console.warn("[MMO] URL intent parse failed:", e);
  }
},

      mmoInit() {
  // HARD SINGLE ROOT ENFORCEMENT
  if (window.__BANGLEZ_MMO_ROOT_ACTIVE__) {
    console.warn("[MMO] Duplicate root blocked:", this.$el);
    this.$el.remove(); // or this.$el.setAttribute("hidden", "true")
    return;
  }

  window.__BANGLEZ_MMO_ROOT_ACTIVE__ = true;

  // existing init logic continues…

        // Root guard: never init same root twice
        if (this.$el && this.$el.hasAttribute("data-mmo-initialized")) return;
        if (this.$el) this.$el.setAttribute("data-mmo-initialized", "true");

        // Version pin: if version changed, wipe saved state to avoid drift
        try {
          const v = safeGet(VERSION_KEY);
          if (v !== VERSION) {
            safeRemove(STORAGE_KEY);
            safeSet(VERSION_KEY, VERSION);
          }
        } catch {}

        const saved = safeParse(safeGet(STORAGE_KEY), null);

        if (saved && typeof saved === "object") {
          this.step = saved.step || "intro";

          const idx = Number(saved.questionIndex);
          this.questionIndex = Number.isFinite(idx) ? idx : 0;

          this.answers = Object.assign(clone(initialAnswers()), saved.answers || {});

          const ids = Array.isArray(saved.flowIds) ? saved.flowIds : [];
          this._flow = idsToFlow(ids, this.answers);

          if (Array.isArray(saved.colorSlots) && saved.colorSlots.length === 3) {
            this.colorSlots = saved.colorSlots;
          } else {
            this._syncSlotsFromAnswers();
          }

          if (this.questionIndex < 0) this.questionIndex = 0;
          if (this.questionIndex > this._flow.length - 1) this.questionIndex = this._flow.length - 1;

          // If user somehow returns to quiz page in "complete", push them to results.
          if (this.step === "complete") this.completeQuiz();

          this._handleEntryIntent();
        return;
        }

        // Fresh start
        this.answers = clone(initialAnswers());
        this._flow = buildFlow(this.answers);
        this.colorSlots = [null, null, null];
        this.step = "intro";
        this.questionIndex = 0;

        this._handleEntryIntent();
      },

      _syncSlotsFromAnswers() {
        const arr = Array.isArray(this.answers.outfit_colors) ? this.answers.outfit_colors : [];
        this.colorSlots = [arr[0] || null, arr[1] || null, arr[2] || null];
      },

      _syncAnswersFromSlots(mode) {
        const slots = [...this.colorSlots];
        if (mode === "one") { slots[1] = null; slots[2] = null; }
        if (mode === "two") { slots[2] = null; }
        this.colorSlots = slots;
        this.answers.outfit_colors = slots.filter(Boolean);
      },

      _save() {
        return persist({
          step: this.step,
          questionIndex: this.questionIndex,
          answers: this.answers,
          colorSlots: this.colorSlots,
          flowIds: flowToIds(this._flow),
        });
      },

      startQuiz() {
        this.step = "quiz";
        this.questionIndex = 0;
        this.answers = clone(initialAnswers());
        this._flow = buildFlow(this.answers);
        this.colorSlots = [null, null, null];
        this.showSizeHelp = false;
        this._save();
      },

      selectOption(option) {
        const q = this.currentQuestion;
        if (!q) return;

        if (q.id === "bangle_size_known" && option === "Not sure — help me measure") {
          this.showSizeHelp = true;
          return;
        }

        if (q.id === "outfit_colors" || q.type === "color_slots") return;

        this.answers[q.id] = option;

        if (q.id === "primary_objective") {
          this.answers.accessory_type = "";
          this.answers.bangle_size_known = "";
          this.answers.bangle_size_value = "";
        }

        if (q.id === "bangle_size_known") {
          this.answers.bangle_size_value = "";
        }

        this._flow = buildFlow(this.answers);
        if (this.questionIndex > this._flow.length - 1) this.questionIndex = this._flow.length - 1;

        if (this.questionIndex < this._flow.length - 1) {
          this.questionIndex += 1;
          this._save();
          return;
        }

        this.completeQuiz();
      },

      selectColor(opt) {
        if (this.colorSlots.includes(opt)) return;
        const idx = this.colorSlots.findIndex((v) => !v);
        if (idx === -1) return;

        this.colorSlots[idx] = opt;
        this.answers.outfit_colors = this.colorSlots.filter(Boolean);
        this._save();
      },

      clearColor(slotIndex) {
        if (slotIndex < 0 || slotIndex > 2) return;
        this.colorSlots[slotIndex] = null;

        const compact = this.colorSlots.filter(Boolean);
        this.colorSlots = [compact[0] || null, compact[1] || null, compact[2] || null];

        this.answers.outfit_colors = this.colorSlots.filter(Boolean);
        this._save();
      },

      finishColors(mode) {
        if (this.selectedColorCount === 0) return;
        this._syncAnswersFromSlots(mode);

        if (this.questionIndex < this._flow.length - 1) {
          this.questionIndex += 1;
          this._save();
          return;
        }

        this.completeQuiz();
      },

      backStep() {
        if (this.questionIndex > 0) {
          this.questionIndex -= 1;
          const q = this._flow[this.questionIndex];
          if (q && (q.id === "outfit_colors" || q.type === "color_slots")) this._syncSlotsFromAnswers();
          this._save();
          return;
        }
        this.step = "intro";
        this._save();
      },

      skipStep() {
        const q = this.currentQuestion;
        if (!q) return;

        if (q.id === "outfit_colors" || q.type === "color_slots") {
          this.colorSlots = [null, null, null];
          this.answers.outfit_colors = [];
        }

        if (q.id === "bangle_size_known") {
          this.answers.bangle_size_known = "";
          this.answers.bangle_size_value = "";
        }

        this._flow = buildFlow(this.answers);

        if (this.questionIndex < this._flow.length - 1) {
          this.questionIndex += 1;
          this._save();
          return;
        }

        this.completeQuiz();
      },

      startOver() {
        safeRemove(STORAGE_KEY);
        safeRemove(VERSION_KEY);
        this.step = "intro";
        this.questionIndex = 0;
        this.answers = clone(initialAnswers());
        this.colorSlots = [null, null, null];
        this._flow = buildFlow(this.answers);
        this.showSizeHelp = false;
      },

      completeQuiz() {
        this.step = "complete";
        const ok = this._save();
        const target = buildResultsUrl();

        const go = () => {
          try { window.location.assign(target); }
          catch { window.location.href = target; }
        };

        // schedule redirect reliably
        Promise.resolve().then(() => requestAnimationFrame(go));

        // fallback if something blocks navigation
        window.setTimeout(() => {
          if (window.location.pathname.includes("/pages/match-my-outfit")) go();
        }, 1200);

        if (!ok) console.warn("[MMO] State save failed before redirect (quota?) — redirecting anyway.");
      },

      async uploadOutfit() {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.style.display = "none";

        const cleanup = () => { try { input.remove(); } catch {} };

        input.addEventListener("change", async () => {
          const file = input.files && input.files[0];
          if (!file) { cleanup(); return; }

          try {
            const dataUrl = await readFileAsDataURL(file);
            const compressed = await compressDataUrl(dataUrl, {
              maxW: 1600, maxH: 1600, quality: 0.82, mime: "image/jpeg",
            });

            this.answers.did_upload_outfit = true;
            this.answers.uploaded_image = compressed;
            this.answers.upload = { name: file.name, type: file.type, size: file.size };
          } catch (e) {
            console.warn("[MMO] Upload failed:", e);
            this.answers.did_upload_outfit = true;
            this.answers.uploaded_image = "";
            this.answers.upload = null;
          }

          this.step = "quiz";
          this.questionIndex = 0;
          this._flow = buildFlow(this.answers);
          this.colorSlots = [null, null, null];
          this.showSizeHelp = false;

          this._save();
          cleanup();
        });

        document.body.appendChild(input);
        input.click();
        window.setTimeout(cleanup, 3000);
      },
    }));

    console.log("✅ MMO Quiz Controller loaded:", VERSION);
  });

// ============================================================
// Mobile Menu — Upload Outfit Trigger
// Ensures upload hotspot opens native file picker
// ============================================================

document.addEventListener('click', function (e) {
  const uploadBtn = e.target.closest('[data-mmo-upload-outfit]');
  if (!uploadBtn) return;

  e.preventDefault();
  e.stopPropagation();

  const fileInput = document.querySelector(
    'input[type="file"][accept^="image"]'
  );

  if (fileInput) {
    fileInput.click();
  }
});

})();
